/**
 * Gatekeeper submission sender. Staff pick the required carrier(s) they're submitting
 * to (any mix of BITCO / EMC / FCCI); this drafts ONE submission email — reviewed once —
 * and sends a personalized copy to each carrier's hard-coded underwriter, with the
 * complete submission attached. Each carrier is marked "Submitted" on send.
 *
 * Liberty is a portal carrier and is handled in the panel (not here).
 */
import { AAS_CONTACT, LOB_NAME, type GatekeeperCarrier, type SubmissionData } from '@commpass/shared';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { fmtDate } from '../lib/format';
import { Modal } from './Modal';
import { useToast } from './Toast';

type Patch = (partial: Partial<SubmissionData>, status?: string) => Promise<void>;

export function GatekeeperEmailModal({
  data,
  patch,
  id,
  carriers,
  onClose,
}: {
  data: SubmissionData;
  patch: Patch;
  id: string;
  carriers: GatekeeperCarrier[]; // email carriers only (Liberty excluded)
  onClose: () => void;
}) {
  const toast = useToast();
  const s = data as Record<string, unknown>;
  const str = (k: string) => String(s[k] ?? '');
  const businessName = str('businessName');
  const lines = Array.isArray(data.lines) ? (data.lines as string[]) : [];
  const linesTxt = lines.map((l) => LOB_NAME[l] ?? l).join(', ') || 'Commercial';

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [building, setBuilding] = useState(true);
  const [busy, setBusy] = useState(false);

  // Attachments: the submission's uploaded docs (any can be excluded before sending).
  const attachable = useMemo(() => {
    const docs = Array.isArray(data.documents) ? (data.documents as Record<string, unknown>[]) : [];
    const reqs = Array.isArray(data.docRequests) ? (data.docRequests as Record<string, unknown>[]) : [];
    const seen = new Set<string>();
    const out: { path: string; name: string }[] = [];
    for (const f of [...docs, ...reqs]) {
      const path = String(f.storagePath ?? '');
      if (!path || seen.has(path)) continue;
      seen.add(path);
      out.push({ path, name: String(f.docName ?? f.fileName ?? f.label ?? 'document') });
    }
    return out;
  }, [data]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  // BITCO stop-gap: BITCO does not treat a submission as complete without loss runs,
  // and will accept a competing agency that submits with them ahead of us. So we hold
  // BITCO out of the send until 5-year loss runs are actually attached.
  const hasLossRuns = useMemo(() => {
    const docs = Array.isArray(data.documents) ? (data.documents as Record<string, unknown>[]) : [];
    return docs.some(
      (d) =>
        d.storagePath &&
        (d.kind === 'lossruns' || /loss ?runs?/i.test(`${String(d.label ?? '')} ${String(d.fileName ?? '')}`)),
    );
  }, [data]);
  const bitcoHeld = carriers.some((c) => c.key === 'bitco') && !hasLossRuns;
  const sendableCarriers = bitcoHeld ? carriers.filter((c) => c.key !== 'bitco') : carriers;

  function fallbackSummary(): string {
    const bits: string[] = [];
    bits.push(
      `${businessName || 'The applicant'}${str('physicalAddress') ? ` of ${str('physicalAddress')}` : ''}${
        str('yearsInBusiness') ? `, in business ${str('yearsInBusiness')} years` : ''
      }.`,
    );
    if (linesTxt) bits.push(`Coverage requested: ${linesTxt}.`);
    if (str('grossAnnualRevenue'))
      bits.push(`Annual revenue ${str('grossAnnualRevenue')}${str('annualPayroll') ? `; payroll ${str('annualPayroll')}` : ''}.`);
    if (str('knownLosses')) bits.push(`Loss history: ${str('knownLosses')}.`);
    return bits.join(' ');
  }

  // Draft the shared submission email once, when the modal opens.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const facts = [
        `Named insured: ${businessName}`,
        `Location: ${str('physicalAddress')}`,
        `Lines requested: ${linesTxt}`,
        `Years in business: ${str('yearsInBusiness')}`,
        `NAICS/SIC: ${str('sicNaics')}`,
        `Annual revenue: ${str('grossAnnualRevenue')}`,
        `Annual payroll: ${str('annualPayroll')}`,
        `Operations: ${str('descriptionOfOps')}`,
        `Loss history: ${str('knownLosses')}`,
        `Effective / need-by: ${str('quoteNeedBy')}`,
        `Target premium: ${str('targetPricing')}`,
      ].join('\n');
      let summary = '';
      try {
        const r = await api.aiExecSummary(facts);
        summary = (r.summary || '').trim();
      } catch {
        /* fall through to template */
      }
      if (!summary) summary = fallbackSummary();

      const snapshot = (
        [
          ['Named insured', businessName || '—'],
          ['Location', str('physicalAddress') || '—'],
          ['Lines requested', linesTxt],
          ['Years in business', str('yearsInBusiness') || '—'],
          ['Annual revenue', str('grossAnnualRevenue') || '—'],
          ['Annual payroll', str('annualPayroll') || '—'],
          ['Effective / need-by', fmtDate(str('quoteNeedBy')) || '—'],
          ['Target premium', str('targetPricing') || '—'],
        ] as [string, string][]
      )
        .map(([k, v]) => `• ${k}: ${v}`)
        .join('\n');

      const draft =
        `Please find a new commercial submission below for your review and quote consideration. Documents are attached.\n\n` +
        `EXECUTIVE SUMMARY\n${summary}\n\n` +
        `DESCRIPTION OF OPERATIONS\n${str('descriptionOfOps') || '(see attached application)'}\n\n` +
        `ACCOUNT SNAPSHOT\n${snapshot}\n\n` +
        (str('knownLosses') ? `LOSS HISTORY\n${str('knownLosses')}\n\n` : '') +
        `Thank you — happy to provide anything further.\n\n` +
        `${AAS_CONTACT.name} — COMM-PASS\n${AAS_CONTACT.email}`;

      if (!alive) return;
      setSubject(`New Commercial Submission — ${businessName || id} (${linesTxt})`);
      setBody(draft);
      setBuilding(false);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send() {
    setBusy(true);
    try {
      const includePaths = attachable.filter((f) => !excluded.has(f.path)).map((f) => f.path);
      let sent = 0;
      let attached = 0;
      const failed: string[] = [];
      for (const c of sendableCarriers) {
        if (!c.uwEmail) continue;
        // Greet the underwriter by first name (e.g. "Hi Tammy,").
        const greet = (c.uwName || c.name).trim().split(/\s+/)[0] || c.name;
        try {
          const r = await api.sendMgaEmail({
            submissionId: id,
            to: c.uwEmail,
            subject,
            body: `Hi ${greet},\n\n${body}`,
            includePaths,
            extraFiles: [],
          });
          if (r.emailed) {
            sent++;
            attached = r.attached ?? attached;
          } else {
            failed.push(c.name);
          }
        } catch {
          failed.push(c.name);
        }
      }

      // Mark each successfully emailed carrier "submitted", in one write, and advance stage.
      const cur = Array.isArray(data.gatekeeperCarriers) ? data.gatekeeperCarriers : [];
      const sentKeys = new Set(
        sendableCarriers.filter((c) => c.uwEmail && !failed.includes(c.name)).map((c) => c.key),
      );
      let nextList = cur.map((x) =>
        sentKeys.has(x.key) ? { ...x, status: 'submitted', at: Date.now() } : x,
      );
      for (const key of sentKeys) {
        if (!nextList.some((x) => x.key === key)) nextList = [...nextList, { key, status: 'submitted', at: Date.now() }];
      }
      await patch(
        { gatekeeperCarriers: nextList, mgaName: sendableCarriers.map((c) => c.name).join(', ') } as Partial<SubmissionData>,
        sent > 0 ? 'mga_sent' : undefined,
      );

      if (sent > 0 && failed.length === 0) toast(`Sent to ${sent} carrier${sent > 1 ? 's' : ''} · ${attached} attachment(s)`);
      else if (sent > 0) toast(`Sent to ${sent}; couldn't send to ${failed.join(', ')}`);
      else toast('Could not send — the email flow may not be configured.');
      onClose();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not send');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      wide
      title="Send Gatekeeper submission"
      subtitle="Each carrier gets its own copy addressed to their underwriter, with the submission attached. Review, then send."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={busy || building || sendableCarriers.length === 0}
            onClick={() => void send()}
          >
            {busy
              ? 'Sending…'
              : building
                ? 'Drafting…'
                : `Send to ${sendableCarriers.length} carrier${sendableCarriers.length === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 10 }}>
        {bitcoHeld && (
          <div
            style={{
              background: '#FEE2E2',
              border: '1px solid #DC2626',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 13,
              lineHeight: 1.5,
              color: '#7F1D1D',
            }}
          >
            <strong>⚠ BITCO is on hold — loss runs required.</strong> BITCO does not treat a submission as complete
            without 5-year loss runs, and will accept a competing agency that has them ahead of us. BITCO will{' '}
            <strong>not</strong> be sent now — attach loss runs to this submission, then submit to BITCO.
            {sendableCarriers.length > 0 ? ' The other carrier(s) below will still send.' : ''}
          </div>
        )}
        <div className="fgroup" style={{ margin: 0 }}>
          <label className="flabel">Sending to</label>
          <div style={{ display: 'grid', gap: 3 }}>
            {carriers.map((c) => {
              const held = bitcoHeld && c.key === 'bitco';
              return (
                <div key={c.key} className="sub-meta" style={held ? { opacity: 0.6 } : undefined}>
                  <b style={{ color: 'var(--navy)' }}>{c.name}</b> — {c.uwName} · {c.uwEmail}
                  {held && <span style={{ color: '#DC2626', fontWeight: 700 }}> — on hold (loss runs required)</span>}
                </div>
              );
            })}
          </div>
        </div>
        <div className="fgroup" style={{ margin: 0 }}>
          <label className="flabel">Subject</label>
          <input className="finput" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="fgroup" style={{ margin: 0 }}>
          <label className="flabel">Email body (editable)</label>
          <textarea
            className="finput"
            rows={12}
            style={{ fontSize: 12.5, lineHeight: 1.5 }}
            value={body}
            placeholder={building ? 'Drafting…' : ''}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="sub-meta" style={{ marginTop: 4 }}>
            Each copy opens with “Hi [first name],” to the carrier's underwriter automatically.
          </div>
        </div>
        <div className="fgroup" style={{ margin: 0 }}>
          <label className="flabel">Attachments</label>
          <div className="sub-meta" style={{ marginBottom: 8 }}>
            {attachable.length > 0
              ? 'The complete submission is attached. Uncheck anything you don’t want to send.'
              : 'No documents are uploaded to this submission yet.'}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {attachable.map((f) => (
              <label key={f.path} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!excluded.has(f.path)}
                  onChange={() =>
                    setExcluded((prev) => {
                      const n = new Set(prev);
                      if (n.has(f.path)) n.delete(f.path);
                      else n.add(f.path);
                      return n;
                    })
                  }
                />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
